import React, { useState, useEffect } from 'react';
import { 
  FileCheck, 
  AlertCircle, 
  Check, 
  X, 
  Info,
  Calendar,
  AlertTriangle,
  User
} from 'lucide-react';

export default function VerificationQueue() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  
  // Rejection modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadDocumentBlob = async (url) => {
    if (!url) return;
    
    setDocumentLoading(true);
    try {
      const fileName = url.split('/').pop();
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/files/${fileName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Impossible de charger le document');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setDocumentBlobUrl(blobUrl);
    } catch (err) {
      console.error('Error loading document:', err);
      setDocumentBlobUrl(null);
    } finally {
      setDocumentLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDoc?.url) {
      // Cleanup previous blob URL
      if (documentBlobUrl) {
        URL.revokeObjectURL(documentBlobUrl);
      }
      loadDocumentBlob(selectedDoc.url);
    }
    
    return () => {
      if (documentBlobUrl) {
        URL.revokeObjectURL(documentBlobUrl);
      }
    };
  }, [selectedDoc?.id]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/documents', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Impossible de charger la file des documents');
      }

      const data = await response.json();
      setDrivers(data);
      
      if (data.length > 0 && !selectedDriver) {
        setSelectedDriver(data[0]);
        if (data[0].documents?.length > 0) {
          setSelectedDoc(data[0].documents[0]);
        }
      } else if (data.length === 0) {
        setSelectedDriver(null);
        setSelectedDoc(null);
      } else if (selectedDriver) {
        // Keep selected driver updated or default to first
        const stillPending = data.find(d => d.driverId === selectedDriver.driverId);
        if (stillPending) {
          setSelectedDriver(stillPending);
          if (stillPending.documents?.length > 0) {
            const stillPendingDoc = stillPending.documents.find(d => d.id === selectedDoc?.id);
            setSelectedDoc(stillPendingDoc || stillPending.documents[0]);
          } else {
            setSelectedDoc(null);
          }
        } else {
          setSelectedDriver(data[0]);
          setSelectedDoc(data[0].documents?.[0] || null);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleApprove = async (docId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/documents/${docId}/validate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la validation du document');
      }

      await fetchDocuments();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    if (!rejectionReason.trim()) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/documents/${selectedDoc.id}/reject`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectionReason })
      });

      if (!response.ok) {
        throw new Error('Erreur lors du rejet du document');
      }

      setRejectModalOpen(false);
      setRejectionReason('');
      await fetchDocuments();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement de la file d'attente...</div>;
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <AlertCircle size={32} color="var(--danger)" />
        <p style={{ marginTop: '12px' }}>{error}</p>
      </div>
    );
  }

  const getDocTypeLabel = (type) => {
    switch (type) {
      case 'VTC_CARD_RECTO': return 'Carte VTC (Recto)';
      case 'VTC_CARD_VERSO': return 'Carte VTC (Verso)';
      case 'DRIVERS_LICENSE_RECTO': return 'Permis de Conduire (Recto)';
      case 'DRIVERS_LICENSE_VERSO': return 'Permis de Conduire (Verso)';
      case 'CARTE_GRISE': return 'Carte Grise (Immatriculation)';
      case 'INSURANCE': return 'Attestation d\'Assurance Professionnelle';
      case 'TECHNICAL_INSPECTION': return 'Contrôle Technique (Visite)';
      default: return type;
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 768px) {
          .vq-queue-layout {
            flex-direction: column !important;
            height: auto !important;
            overflow: visible !important;
            gap: 16px !important;
          }
          .vq-sidebar {
            max-width: 100% !important;
            min-width: 100% !important;
            max-height: 200px !important;
            flex: none !important;
          }
          .vq-preview-panel {
            min-width: 100% !important;
            flex: none !important;
            overflow: visible !important;
          }
          .vq-preview-header {
            padding: 14px 16px !important;
          }
          .vq-preview-content {
            padding: 14px !important;
          }
          .vq-header-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .vq-action-buttons {
            width: 100% !important;
            flex-direction: column !important;
          }
          .vq-action-buttons button {
            width: 100% !important;
            justify-content: center !important;
          }
          .vq-content-grid {
            flex-direction: column-reverse !important;
          }
          .vq-image-container {
            min-height: 260px !important;
            max-height: 380px !important;
          }
          .vq-modal-content {
            width: 94% !important;
            max-width: 94% !important;
            padding: 18px !important;
            margin: 12px !important;
          }
        }
      `}</style>
      {drivers.length === 0 ? (
        <div style={styles.emptyContainer} className="glass-card">
          <FileCheck size={48} color="var(--success)" />
          <h3 style={{ marginTop: '16px', fontFamily: 'var(--font-title)' }}>File d'attente vide</h3>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>Tous les documents de chauffeurs ont été traités. SLA respecté !</p>
        </div>
      ) : (
        <div style={styles.queueLayout} className="vq-queue-layout">
          {/* Left panel: List of pending drivers */}
          <div style={styles.sidebarPanel} className="glass-card vq-sidebar">
            <div style={styles.panelTitleContainer}>
              <span style={styles.panelHeaderTitle}>Chauffeurs en attente ({drivers.length})</span>
            </div>
            <div style={styles.docList}>
              {drivers.map((driver) => {
                const isSelected = selectedDriver?.driverId === driver.driverId;
                return (
                  <div 
                    key={driver.driverId} 
                    onClick={() => {
                      setSelectedDriver(driver);
                      if (driver.documents?.length > 0) {
                        setSelectedDoc(driver.documents[0]);
                      }
                    }}
                    style={{
                      ...styles.docListItem,
                      ...(isSelected ? styles.docListItemActive : {})
                    }}
                  >
                    <div style={styles.docListItemMeta}>
                      <span style={styles.driverNameLabel}>{driver.firstName} {driver.lastName}</span>
                      <span style={styles.docTypeLabel}>{driver.email}</span>
                    </div>
                    <span style={styles.badgePending}>{driver.documents?.length} doc(s)</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel: Active driver & documents preview */}
          <div style={styles.previewPanel} className="glass-card vq-preview-panel">
            {selectedDriver && (
              <>
                <div style={styles.previewHeader} className="vq-preview-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={styles.driverAvatar}>
                      <User size={24} color="#0052ff" />
                    </div>
                    <div>
                      <h3 style={styles.previewTitle}>{selectedDriver.firstName} {selectedDriver.lastName}</h3>
                      <p style={styles.previewSubtitle}>{selectedDriver.email} • {selectedDriver.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Documents Tabs */}
                {selectedDriver.documents?.length > 0 && (
                  <div style={styles.docTabsContainer}>
                    {selectedDriver.documents.map(doc => {
                      const isActive = selectedDoc?.id === doc.id;
                      return (
                        <div 
                          key={doc.id}
                          onClick={() => setSelectedDoc(doc)}
                          style={{
                            ...styles.docTab,
                            ...(isActive ? styles.docTabActive : {})
                          }}
                        >
                          {getDocTypeLabel(doc.type)}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Document Preview Content */}
                {selectedDoc && (
                  <div style={styles.previewContent} className="vq-preview-content">
                    <div className="vq-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h4 style={styles.sectionHeader}>{getDocTypeLabel(selectedDoc.type)}</h4>
                      <div style={styles.actionButtons} className="vq-action-buttons">
                        <button 
                          onClick={() => handleApprove(selectedDoc.id)}
                          disabled={actionLoading}
                          style={styles.approveBtn}
                          className="btn-primary"
                        >
                          <Check size={16} />
                          <span>Approuver ce document</span>
                        </button>
                        <button 
                          onClick={() => setRejectModalOpen(true)}
                          disabled={actionLoading}
                          style={styles.rejectBtn}
                        >
                          <X size={16} />
                          <span>Rejeter ce document</span>
                        </button>
                      </div>
                    </div>

                    <div style={styles.contentGrid} className="vq-content-grid">
                      <div style={styles.metaContainer}>
                        {selectedDoc.expiryDate && (
                          <div style={styles.expiryBox}>
                            <Calendar size={16} color="var(--primary)" style={{ marginRight: '8px' }} />
                            <strong>Date d'expiration : </strong>
                            <span style={{ marginLeft: '4px' }}>{selectedDoc.expiryDate}</span>
                          </div>
                        )}
                        <span style={styles.sectionHeader}>Règles de conformité</span>
                        <div style={styles.ruleList}>
                          <div style={styles.ruleItem}>
                            <Check size={14} color="var(--success)" />
                            <span>Le nom correspond à la fiche d'inscription.</span>
                          </div>
                          <div style={styles.ruleItem}>
                            <Check size={14} color="var(--success)" />
                            <span>Le document est en cours de validité (non expiré).</span>
                          </div>
                          <div style={styles.ruleItem}>
                            <Check size={14} color="var(--success)" />
                            <span>Les 4 coins de la carte/du document sont visibles.</span>
                          </div>
                        </div>

                        <div style={styles.warningBox}>
                          <AlertTriangle size={18} color="var(--warning)" />
                          <div style={styles.warningTextContainer}>
                            <span style={styles.warningTitle}>Vigilance</span>
                            <span style={styles.warningSub}>Toute décision de validation ou de rejet est immédiatement consignée dans les logs de traçabilité d'audit.</span>
                          </div>
                        </div>
                      </div>

                      <div style={styles.imageViewContainer} className="vq-image-container">
                        {documentLoading ? (
                          <div style={styles.noImagePlaceholder}>
                            <Info size={32} />
                            <span>Chargement du document...</span>
                          </div>
                        ) : documentBlobUrl ? (
                          (() => {
                            const fileName = selectedDoc.url.split('/').pop();
                            const isPdf = fileName.toLowerCase().endsWith('.pdf');
                            
                            return isPdf ? (
                              <iframe 
                                src={documentBlobUrl} 
                                style={styles.docImage}
                                title={getDocTypeLabel(selectedDoc.type)}
                              />
                            ) : (
                              <img 
                                src={documentBlobUrl} 
                                alt={getDocTypeLabel(selectedDoc.type)} 
                                style={{ ...styles.docImage, objectFit: 'contain' }}
                              />
                            );
                          })()
                        ) : (
                          <div style={styles.noImagePlaceholder}>
                            <Info size={32} />
                            <span>Aperçu indisponible</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="glass-card vq-modal-content">
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Rejeter le document</h3>
              <button onClick={() => setRejectModalOpen(false)} style={styles.closeModalBtn}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleRejectSubmit} style={styles.modalForm}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Motif du rejet (obligatoire)</label>
                <textarea
                  placeholder="Ex: Document expiré, photo floue, nom ne correspondant pas à l'inscription..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  style={styles.textarea}
                  className="input-field"
                  rows={4}
                  required
                />
              </div>
              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setRejectModalOpen(false)}
                  style={styles.cancelBtn}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading || !rejectionReason.trim()}
                  style={styles.confirmRejectBtn}
                >
                  Confirmer le rejet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    textAlign: 'center',
    margin: 'auto',
    maxWidth: '500px',
  },
  queueLayout: {
    display: 'flex',
    gap: '20px',
    flex: 1,
    minHeight: 0,
  },
  sidebarPanel: {
    flex: 1,
    minWidth: '260px',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelTitleContainer: {
    padding: '16px 20px',
    borderBottom: '0.5px solid var(--border-color)',
  },
  panelHeaderTitle: {
    fontFamily: 'var(--font-title)',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: 'var(--text-primary)',
  },
  docList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  docListItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '0.5px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  docListItemActive: {
    backgroundColor: 'rgba(0, 82, 255, 0.05)',
    borderLeft: '4px solid var(--primary)',
  },
  docListItemMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  driverNameLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  docTypeLabel: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
  },
  badgePending: {
    fontSize: '0.65rem',
    padding: '2px 8px',
    borderRadius: '9999px',
    backgroundColor: 'var(--warning-bg)',
    border: '0.5px solid var(--warning-border)',
    color: 'var(--warning)',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  previewPanel: {
    flex: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '0.5px solid var(--border-color)',
    backgroundColor: '#fafafa',
  },
  driverAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(0, 82, 255, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewTitle: {
    fontSize: '1.2rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  previewSubtitle: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    marginTop: '4px',
  },
  docTabsContainer: {
    display: 'flex',
    gap: '8px',
    padding: '12px 24px',
    borderBottom: '0.5px solid var(--border-color)',
    backgroundColor: '#f1f5f9',
    overflowX: 'auto',
  },
  docTab: {
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    backgroundColor: '#e2e8f0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  docTabActive: {
    color: '#0052ff',
    backgroundColor: 'rgba(0, 82, 255, 0.1)',
    border: '1px solid rgba(0, 82, 255, 0.2)',
  },
  actionButtons: {
    display: 'flex',
    gap: '12px',
  },
  approveBtn: {
    padding: '10px 18px',
  },
  rejectBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    color: 'var(--danger)',
    border: '0.5px solid var(--border-color)',
    padding: '10px 18px',
    fontFamily: 'var(--font-title)',
    fontWeight: '500',
    fontSize: '0.95rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  previewContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: '24px',
  },
  contentGrid: {
    display: 'flex',
    gap: '24px',
    width: '100%',
    flex: 1,
    minHeight: 0,
  },
  imageViewContainer: {
    flex: 2,
    height: '100%',
    minHeight: '400px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  docImage: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  noImagePlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    color: 'var(--text-muted)',
    fontSize: '0.85rem',
  },
  metaContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '250px',
  },
  sectionHeader: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: 0.05,
  },
  ruleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '0.85rem',
    color: 'var(--text-primary)',
  },
  warningBox: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'var(--warning-bg)',
    border: '0.5px solid var(--warning-border)',
    borderRadius: 'var(--radius-sm)',
  },
  warningTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  warningTitle: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--warning)',
  },
  warningSub: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    lineHeight: '1.4',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modalContent: {
    width: '100%',
    maxWidth: '480px',
    padding: '28px',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '16px',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  closeModalBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '4px',
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  textarea: {
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
    lineHeight: '1.5',
    resize: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '0.5px solid var(--border-color)',
    paddingTop: '16px',
    marginTop: '10px',
  },
  cancelBtn: {
    padding: '10px 20px',
  },
  confirmRejectBtn: {
    backgroundColor: 'var(--danger)',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    fontFamily: 'var(--font-title)',
    fontWeight: '500',
    fontSize: '0.95rem',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  expiryBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border-color)',
    marginBottom: '20px',
    fontSize: '0.9rem',
    color: 'var(--text-primary)',
  }
};
